import { ReactNode, useEffect, useMemo } from 'react'
import { Grid, ThemeProvider } from '@mui/material'
import { ReactComponent as WalterLearning } from '../../assets/WalterLearning.svg'
import { ReactComponent as WalterSanteColor } from '../../assets/WalterSanteColor.svg'
import { ReactComponent as LogoEdumiam } from '../../assets/edumiam.svg'
import { learningPhoneNumber, santePhoneNumber } from '../../utils/constants'
import { BrandDomainType, buildMarketplaceTheme } from '../../utils/artDirection'
import CallUs from '../CallUs'

type MarketplaceShellProps = {
  /** Habillage de marque, distinct du domaine de catalogue (Edumiam, hors de son domaine, se rend en Learning). */
  displayDomain: BrandDomainType
  /** Réserve la place de la barre de panier quand elle est visible. */
  cartCount: number
  children: ReactNode
}

/** Habillage commun aux deux marketplaces : thème, logo, numéro d'appel, gouttière.
 *
 * Les routes `/groupe/...` sont hors du pattern `/sante?/*` porté par App.tsx : sans ce ThemeProvider
 * imbriqué, une marketplace santé s'afficherait avec le thème noir Learning. Le thème dérivé porte en
 * plus la direction artistique des sites vitrines (cf. docs/direction-artistique.md). */
const MarketplaceShell = ({ displayDomain, cartCount, children }: MarketplaceShellProps) => {
  const muiTheme = useMemo(() => buildMarketplaceTheme(displayDomain), [displayDomain])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <ThemeProvider theme={muiTheme}>
      <Grid
        container
        justifyContent="center"
        sx={{
          minHeight: '100vh',
          p: 2,
          pb: cartCount > 0 ? 16 : 2,
          backgroundColor: 'background.default',
        }}
      >
        <Grid sx={{ position: 'absolute', top: 0, left: 0, pt: 2, pl: { xs: 2, md: 4 } }}>
          {displayDomain === 'edumiam' ? (
            <LogoEdumiam width={180} height={48} style={{ display: 'block', overflow: 'visible' }} />
          ) : displayDomain === 'sante' ? (
            <WalterSanteColor width="162px" />
          ) : (
            <WalterLearning width="180px" />
          )}
        </Grid>

        {/* CallUs ne s'affiche pas sur le domaine Edumiam (pas de numéro sur les pages Edumiam). */}
        <Grid sx={{ position: 'absolute', top: 0, right: 0, mt: 2, mr: 4, display: { xs: 'none', md: 'block' } }}>
          <CallUs phone={displayDomain === 'sante' ? santePhoneNumber : learningPhoneNumber} />
        </Grid>

        <Grid item xs={12} md={10} sx={{ mt: { xs: 15, md: 12 }, mb: 6 }}>
          {children}
        </Grid>
      </Grid>
    </ThemeProvider>
  )
}

export default MarketplaceShell
